import {
  CheckCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  Download,
  FileText,
  MessageSquare,
  ShieldCheck,
  TrendingUp,
  Send,
} from "lucide-react";
import "chart.js/auto";
import { Doughnut, Line } from "react-chartjs-2";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import providerProjectService from "../../../api/manpro/providerProjectService";
import { useProjectManagementContext } from "../context";
import { isInternalManproProject } from "../isInternalManproProject";
import ProjectSectionShell from "./ProjectSectionShell";

const ReportingSection = () => {
  const {
    baselineData,
    executionData,
    handleSaveManagerNote,
    managerNote,
    setManagerNote,
    totalCalculatedExpense,
    allProjects,
    activeProjectId,
    activeProjectData,
  } = useProjectManagementContext() as any;

  const listProject = allProjects?.find((p: any) => String(p.id) === String(activeProjectId));
  const activeProject = activeProjectData || listProject;
  const isExecutionLocked = activeProject && activeProject.status !== 'active';
  const isPrivateProject = isInternalManproProject(activeProject);

  const queryClient = useQueryClient();

  const completeMutation = useMutation({
    mutationFn: (id: string) => providerProjectService.completeProject(id),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Proyek berhasil diselesaikan");
      queryClient.invalidateQueries({ queryKey: ["providerManproProjects"] });
      queryClient.invalidateQueries({ queryKey: ["project-management", "project-tree"] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Gagal menyelesaikan proyek");
    },
  });

  const requestHandoverMutation = useMutation({
    mutationFn: (id: string) => providerProjectService.requestHandover(id),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Berhasil mengajukan Serah Terima (BAST)");
      queryClient.invalidateQueries({ queryKey: ["providerManproProjects"] });
      queryClient.invalidateQueries({ queryKey: ["project-management", "project-tree"] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Gagal mengajukan Serah Terima");
    },
  });

  const isMutating = completeMutation.isPending || requestHandoverMutation.isPending;

  return (() => {
    // === TAB 4 DATA PIPELINE ===
    // Approved tasks from Tab 3
    const approvedTaskIds =
      executionData.validatedTasks || [];
    const approvedTasks = baselineData.tasks.filter((t: any) =>
      approvedTaskIds.includes(t.id),
    );
    const totalTasks = baselineData.tasks.length;
    const approvedCount = approvedTasks.length;

    // Physical Progress (only approved)
    const physicalProgress =
      totalTasks > 0
        ? Math.round(
          approvedTasks.reduce((sum: number, t: any) => {
            const m = baselineData.milestones.find(
              (ms: any) => ms.id === t.milestone_id,
            );
            return ( sum + (m ? (Number(m.weight) * Number(t.weight)) / 100 : 0)
            );
          }, 0),
        )
        : 0;

    // Time Remaining
    const today = new Date();
    const deadlineDate = baselineData.deadline
      ? new Date(baselineData.deadline)
      : null;
    const daysRemaining = deadlineDate
      ? Math.max(
        0,
        Math.ceil(
          (deadlineDate.getTime() - today.getTime()) /
          (1000 * 60 * 60 * 24),
        ),
      )
      : null;

    // Cost Efficiency
    const savings =
      baselineData.budget - totalCalculatedExpense;

    // SLA Compliance
    const slaScore = approvedCount > 0 ? 100 : 0; // 100% if all approved tasks passed all 3 checklist items

    // S-Curve data (Dynamic)
    let startDate = new Date();
    if (baselineData.kickoff_date) startDate = new Date(baselineData.kickoff_date);
    else if (baselineData.created_at) startDate = new Date(baselineData.created_at);

    const endDate = deadlineDate || new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days fallback

    const timeLabels: string[] = [];
    const baselineCurve: number[] = [];
    const actualData: (number | null)[] = [];

    let currentMonth = new Date(startDate);
    currentMonth.setDate(1); // Set to 1st of month to avoid overflow

    let totalMonths = 0;
    while (currentMonth <= endDate || totalMonths === 0) {
      totalMonths++;
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
    totalMonths = Math.max(1, Math.min(totalMonths, 24)); // Cap at 24 months for UI sanity

    // Generate Baseline Curve (Sigmoid approximation)
    for (let i = 0; i < totalMonths; i++) {
      const d = new Date(startDate);
      d.setMonth(d.getMonth() + i);
      timeLabels.push(d.toLocaleDateString("id-ID", { month: "short", year: "2-digit" }));

      // Sigmoid from 0 to 100
      const x = (i / (totalMonths - 1 || 1)) * 10 - 5; // maps to range [-5, 5]
      const sigmoid = 1 / (1 + Math.exp(-x));
      baselineCurve.push(Math.round(sigmoid * 100));
    }

    // Generate Actual Data (Realization)
    const currentMonthLabel = today.toLocaleDateString("id-ID", { month: "short", year: "2-digit" });
    let passedCurrentMonth = false;

    for (let i = 0; i < timeLabels.length; i++) {
      if (passedCurrentMonth) {
        actualData.push(null); // Future months
      } else {
        if (timeLabels[i] === currentMonthLabel) {
          actualData.push(physicalProgress);
          passedCurrentMonth = true;
        } else {
          // Estimate past progress relative to baseline
          const b = baselineCurve[i];
          const bCurrent = baselineCurve[timeLabels.indexOf(currentMonthLabel)] || 100;
          actualData.push(Math.round(Math.min(physicalProgress, b * (physicalProgress / bCurrent))));
        }
      }
    }

    const sCurveDataTab4 = {
      labels: timeLabels,
      datasets: [
        {
          label: "Rencana Kerja Final",
          data: baselineCurve,
          borderColor: "#94a3b8",
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 3,
          backgroundColor: "transparent",
          tension: 0.4,
        },
        {
          label: "Realisasi (Approved)",
          data: actualData,
          borderColor: "#2563eb",
          borderWidth: 3,
          pointRadius: 4,
          pointBackgroundColor: "#2563eb",
          backgroundColor: "rgba(37, 99, 235, 0.08)",
          fill: true,
          tension: 0.4,
        },
      ],
    };

    const burnRateData = {
      labels: ["Terpakai", "Sisa Budget"],
      datasets: [
        {
          data: [
            Math.max(0, totalCalculatedExpense),
            Math.max(
              0,
              baselineData.budget - totalCalculatedExpense,
            ),
          ],
          backgroundColor: ["#3b82f6", "#e0f2fe"],
          borderWidth: 0,
        },
      ],
    };

    const milestoneTimeline = approvedTasks.map((t: any) => ({
      name: t.name,
      date: t.updated_at ? new Date(t.updated_at).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }) : today.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      milestone:
        baselineData.milestones.find(
          (m: any) => m.id === t.milestone_id,
        )?.label || "Milestone",
    }));

    const kpiCards = [
      {
        label: "Progress Fisik",
        value: `${physicalProgress}%`,
        sub: `${approvedCount} dari ${totalTasks} tugas selesai`,
        color: "#2563eb",
        bg: "#eff6ff",
        icon: <TrendingUp size={20} />,
      },
      {
        label: "Sisa Waktu",
        value:
          daysRemaining !== null
            ? `${daysRemaining} Hari`
            : "Deadline Tidak ada",
        sub: deadlineDate
          ? `Deadline: ${deadlineDate.toLocaleDateString("id-ID")}`
          : "Deadline belum diset",
        color:
          daysRemaining !== null && daysRemaining < 10
            ? "#ef4444"
            : "#10b981",
        bg:
          daysRemaining !== null && daysRemaining < 10
            ? "#fef2f2"
            : "#f0fdf4",
        icon: <Clock size={20} />,
      },
      {
        label: "Efisiensi Biaya",
        value:
          savings >= 0
            ? `Hemat ${new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0, notation: "compact" }).format(savings)}`
            : `Overbudget ${new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0, notation: "compact" }).format(Math.abs(savings))}`,
        sub: `Budget: ${new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0, notation: "compact" }).format(baselineData.budget)}`,
        color: savings >= 0 ? "#10b981" : "#ef4444",
        bg: savings >= 0 ? "#f0fdf4" : "#fef2f2",
        icon: <DollarSign size={20} />,
      },
      {
        label: "SLA Compliance",
        value: `${slaScore}%`,
        sub:
          approvedCount > 0
            ? "Semua tugas lulus checklist"
            : "Belum ada tugas approved",
        color: "#f59e0b",
        bg: "#fffbeb",
        icon: <ShieldCheck size={20} />,
      },
    ];

    // Calculate expected baseline for today
    const currentMonthIndex = timeLabels.indexOf(currentMonthLabel);
    const expectedProgress = currentMonthIndex >= 0 ? baselineCurve[currentMonthIndex] : 0;
    const progressDeviation = physicalProgress - expectedProgress;

    const projectStatus =
      progressDeviation >= -5
        ? "PROYEK SEHAT"
        : progressDeviation >= -15
          ? "PERLU PERHATIAN"
          : "PROYEK KRITIS";
    const statusColor =
      progressDeviation >= -5
        ? "#10b981"
        : progressDeviation >= -15
          ? "#f59e0b"
          : "#ef4444";
    const statusBg =
      progressDeviation >= -5
        ? "#f0fdf4"
        : progressDeviation >= -15
          ? "#fffbeb"
          : "#fef2f2";

    return (
      <ProjectSectionShell
        eyebrow="Laporan & Serah Terima"
        title="Laporan Proyek dan KPI Delivery"
        description="Konsolidasikan progress approved, efisiensi biaya, SLA, kurva-S, timeline milestone, dan catatan manager."
        icon={<FileText size={14} />}
        tone="violet"
      >
        <div className="space-y-6">
          {/* Header Row: Title + Status Badge + Export Buttons */}
          <div
            className="flex justify-between items-center gap-3" style={{ marginBottom: "28px", flexWrap: "wrap", }}
          >
            <div>
              <h2
                className="m-0 font-extrabold text-slate-800" style={{ fontSize: "1.3rem", }}
              >
                Laporan & Output Proyek
              </h2>
              <p
                className="mt-1 text-slate-500 text-[0.9rem]"
              >
                Data dari pekerjaan yang telah divalidasi di Tab
                3
              </p>
            </div>
            <div
              className="flex items-center" style={{ gap: "10px", }}
            >
              {/* Status Badge */}
              <span
                className="font-bold text-[0.85rem]" style={{ padding: "8px 16px", borderRadius: "20px", backgroundColor: statusBg, color: statusColor, border: `1.5px solid ${statusColor}`, }}
              >
                {projectStatus}
              </span>
              {/* Export Buttons */}
              {/* <button
                onClick={() =>
                  toast("Fitur Download PDF akan segera tersedia.")
                }
                className="flex items-center px-4 py-2.5 text-white border-none rounded-[10px] font-semibold text-[0.85rem] cursor-pointer" style={{ gap: "6px", backgroundColor: "#1e293b", }}
              >
                <Download size={15} /> PDF
              </button>
              <button
                onClick={() =>
                  toast("Fitur Export Excel akan segera tersedia.")
                }
                className="flex items-center px-4 py-2.5 bg-green-600 text-white border-none rounded-[10px] font-semibold text-[0.85rem] cursor-pointer" style={{ gap: "6px", }}
              >
                <FileText size={15} /> Excel
              </button> */}
            </div>
          </div>

          {/* KPI Cards Row */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
          >
            {kpiCards.map((kpi, i) => (
              <div
                key={i}
                className="bg-white rounded-[14px] p-5 shadow-sm border border-slate-100"
              >
                <div
                  className="flex justify-between items-start mb-3"
                >
                  <div
                    className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ backgroundColor: kpi.bg, color: kpi.color, }}
                  >
                    {kpi.icon}
                  </div>
                </div>
                <div
                  className="text-2xl font-extrabold mb-1" style={{ color: kpi.color, }}
                >
                  {kpi.value}
                </div>
                <div
                  className="text-sm font-semibold text-slate-800 mb-0.5"
                >
                  {kpi.label}
                </div>
                <div
                  className="text-xs text-slate-400"
                >
                  {kpi.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Main Charts Row */}
          <div
            className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5 mb-6"
          >
            {/* S-Curve */}
            <div
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <div
                className="flex justify-between items-start mb-2"
              >
                <div>
                  <h3
                    className="m-0 text-base font-bold text-slate-800"
                  >
                    Performance Analysis (Kurva-S)
                  </h3>
                  <p
                    className="mt-1 text-[0.82rem] text-slate-400"
                  >
                    Rencana Kerja Final vs Realisasi Disetujui
                  </p>
                </div>
                <div
                  className="flex gap-4 text-xs"
                >
                  <span
                    className="flex items-center" style={{ gap: "5px", }}
                  >
                    <span
                      className="w-5 h-[2px]" style={{ borderTop: "2px dashed #94a3b8", display: "inline-block", }}
                    ></span>{" "}
                    Rencana
                  </span>
                  <span
                    className="flex items-center" style={{ gap: "5px", }}
                  >
                    <span
                      className="w-5 h-[3px]" style={{ backgroundColor: "#2563eb", display: "inline-block", borderRadius: "2px", }}
                    ></span>{" "}
                    Realisasi
                  </span>
                </div>
              </div>
              <div className="h-[260px]">
                <Line
                  id="s-curve-chart"
                  data={sCurveDataTab4}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: {
                        min: 0,
                        max: 100,
                        ticks: {
                          callback: (v) => `${v}%`,
                          font: { size: 11 },
                        },
                        grid: { color: "#f1f5f9" },
                      },
                      x: {
                        ticks: { font: { size: 11 } },
                        grid: { display: false },
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Budget Burn Rate Donut */}
            <div
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col"
            >
              <h3
                className="mb-1 text-base font-bold text-slate-800"
              >
                Budget Burn Rate
              </h3>
              <p
                className="mb-4 text-[0.82rem] text-slate-400"
              >
                Serapan anggaran vs sisa
              </p>
              <div
                className="relative flex-1" style={{ minHeight: "180px", }}
              >
                <Doughnut
                  id="budget-burn-rate-chart"
                  data={burnRateData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          font: { size: 12 },
                          usePointStyle: true,
                        },
                      },
                    },
                    cutout: "68%",
                  }}
                />
                <div
                  className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 text-center"
                >
                  <div
                    className="text-xl font-extrabold text-slate-800"
                  >
                    {baselineData.budget > 0
                      ? Math.round(
                        (totalCalculatedExpense /
                          baselineData.budget) *
                        100,
                      )
                      : 0}
                    %
                  </div>
                  <div
                    className="text-[0.7rem] text-slate-400"
                  >
                    Terpakai
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: Timeline + Manager Note */}
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-5"
          >
            {/* Milestone Timeline */}
            <div
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <h3
                className="mb-4 text-base font-bold text-slate-800 flex items-center gap-2"
              >
                <CheckCircle
                  size={18}
                  className="text-emerald-500"
                />{" "}
                Historical Milestones
              </h3>
              {milestoneTimeline.length === 0 ? (
                <div
                  className="text-center py-8 px-4 text-slate-400"
                >
                  <CheckCircle2
                    size={32}
                    className="mb-2 opacity-30"
                  />
                  <p style={{ margin: 0, fontSize: "0.9rem", }}>
                    Belum ada pekerjaan yang di-Approve.
                  </p>
                  <p
                    className="mt-1 text-sm"
                  >
                    Selesaikan validasi di Tab 3 terlebih
                    dahulu.
                  </p>
                </div>
              ) : (
                <div
                  className="flex flex-col gap-3"
                >
                  {milestoneTimeline.map((item: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 py-3 px-[14px] bg-green-50 rounded-[10px] border border-green-200"
                    >
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: "#10b981", }}
                      ></div>
                      <div>
                        <div
                          className="font-bold text-[0.9rem] text-slate-800"
                        >
                          {item.name}
                        </div>
                        <div
                          className="text-[0.78rem] text-slate-500 mt-0.5"
                        >
                          {item.milestone} — Approved{" "}
                          {item.date}
                        </div>
                      </div>
                      <CheckCircle
                        size={16}
                        className="text-emerald-500 ml-auto mt-1 shrink-0"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manager Note */}
            <div
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col"
            >
              <h3
                className="mb-1.5 text-base font-bold text-slate-800 flex items-center gap-2"
              >
                <MessageSquare
                  size={18}
                  className="text-blue-600"
                />{" "}
                Pesan Manajer Proyek
              </h3>
              <p
                className="mb-3.5 text-[0.82rem] text-slate-400"
              >
                Narasi penjelasan situasi proyek untuk owner /
                klien
              </p>
              <textarea
                value={managerNote}
                onChange={(e) => setManagerNote(e.target.value)}
                placeholder="Contoh: Progres saat ini sesuai target. Ada keterlambatan kecil pada pengiriman alat namun sudah teratasi. Estimasi selesai tepat waktu..."
                className={`flex-1 min-h-[160px] w-full p-3.5 text-[0.9rem] border-[1.5px] border-slate-200 rounded-[10px] resize-y font-inherit leading-relaxed outline-none ${isExecutionLocked ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-slate-50 text-slate-800'}`}
                disabled={isExecutionLocked}
              />
              {!isExecutionLocked && (
                <button
                  onClick={handleSaveManagerNote}
                  className="mt-3 p-2.5 text-white border-none font-semibold cursor-pointer text-[0.9rem] bg-blue-600 hover:bg-blue-700 rounded-md transition-all duration-200"
                >
                  Simpan Catatan
                </button>
              )}
            </div>
          </div>

          {/* Serah Terima / Penutupan Proyek */}
          <div className="mt-5 bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-sm border border-blue-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="mb-2 text-lg font-extrabold text-blue-900 flex items-center gap-2">
                <ShieldCheck size={20} className="text-blue-600" />
                {isPrivateProject ? "Tutup Buku Proyek Internal" : "Tahap Akhir: Serah Terima Proyek"}
              </h3>
              <p className="text-sm text-slate-600 max-w-xl leading-relaxed">
                {isPrivateProject 
                  ? "Proyek ini adalah proyek internal Anda (tanpa Owner/Klien di platform). Anda dapat langsung menyatakan proyek ini selesai dan menutup buku."
                  : "Pastikan seluruh Pekerjaan telah divalidasi dan catatan manager sudah tersimpan sebelum mengajukan Berita Acara Serah Terima (BAST) kepada Owner."
                }
              </p>
            </div>
            
            <button
              onClick={() => {
                 if (!activeProjectId) return;
                 if (isPrivateProject) {
                   completeMutation.mutate(activeProjectId.toString());
                 } else {
                   requestHandoverMutation.mutate(activeProjectId.toString());
                 }
              }}
              disabled={isExecutionLocked || isMutating}
              className={`shrink-0 px-6 py-3.5 rounded-xl font-bold text-white shadow-sm transition-all duration-200 flex items-center gap-2 ${
                isExecutionLocked 
                  ? "bg-slate-300 cursor-not-allowed" 
                  : isMutating
                  ? "bg-slate-400 cursor-wait"
                  : isPrivateProject
                    ? "bg-emerald-600 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-200 active:scale-95"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-200 active:scale-95"
              }`}
            >
              {isMutating ? (
                "Memproses..."
              ) : isExecutionLocked ? (
                "Proyek Terkunci"
              ) : isPrivateProject ? (
                <>
                  <CheckCircle2 size={18} />
                  Selesaikan Proyek
                </>
              ) : (
                <>
                  <Send size={18} />
                  Ajukan BAST / Serah Terima
                </>
              )}
            </button>
          </div>

        </div>
      </ProjectSectionShell>
    );
  })();
};

export default ReportingSection;
